import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { normalizeGenerationProgress } from '../../util/generationRecords';

type GenerationProgressProps = {
  progress?: number | null;
  size: number | string;
  thickness?: number;
  color?: string;
  trackColor?: string;
  fontSize?: number | string | object;
  sx?: Record<string, unknown>;
};

export const hasMeasuredGenerationProgress = (progress: unknown): boolean =>
  normalizeGenerationProgress(progress) > 1;

const GenerationProgress: React.FC<GenerationProgressProps> = ({
  progress,
  size,
  thickness = 4,
  color = '#76A9E7',
  trackColor = 'rgba(255,255,255,0.18)',
  fontSize = 10,
  sx,
}) => {
  const progressValue = normalizeGenerationProgress(progress);
  const hasProgress = progressValue > 1;

  return (
    <Box
      aria-label={hasProgress ? `Generating, ${progressValue}% complete` : 'Generating'}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 2,
        ...sx,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ position: 'absolute', color: trackColor }}
      />
      <CircularProgress
        variant={hasProgress ? 'determinate' : 'indeterminate'}
        value={hasProgress ? progressValue : undefined}
        size={size}
        thickness={thickness}
        sx={{ position: 'absolute', color }}
      />
      {hasProgress && (
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize,
            fontWeight: 950,
            lineHeight: 1,
            textShadow: '0 1px 5px rgba(0,0,0,0.76)',
          }}
        >
          {progressValue}%
        </Typography>
      )}
    </Box>
  );
};

export default GenerationProgress;
